import { httpClient } from "@/services";

// ----- Permissions normalizer (priority sorting) -----

// ключ = id батька, значення = масив id дітей у потрібному порядку
const PERMISSIONS_PRIORITY = {
	18: [ 10 ], // Trips -> View першою
};

const sortChildrenByPriority = (children, priorityIds) => {
	if (!Array.isArray(children) || !Array.isArray(priorityIds) || !priorityIds.length) return children;

	const priorityIndex = {};
	for (let i = 0; i < priorityIds.length; i++) {
		priorityIndex[priorityIds[i]] = i;
	}

	return children
		.map((item, originalIndex) => ({ item, originalIndex }))
		.sort((a, b) => {
			const ai = priorityIndex[a.item && a.item.id];
			const bi = priorityIndex[b.item && b.item.id];

			const aHas = ai !== undefined;
			const bHas = bi !== undefined;

			if (aHas && bHas) return ai - bi;
			if (aHas && !bHas) return -1;
			if (!aHas && bHas) return 1;
			return a.originalIndex - b.originalIndex;
		})
		.map(x => x.item);
};

const normalizePermissionsTree = (nodes) => {
	const walk = (node) => {
		if (!node) return;

		const priority = PERMISSIONS_PRIORITY[node.id];
		if (priority && Array.isArray(node.children)) {
			node.children = sortChildrenByPriority(node.children, priority);
		}

		if (Array.isArray(node.children)) {
			node.children.forEach(walk);
		}
	};

	if (Array.isArray(nodes)) nodes.forEach(walk);
	return nodes;
};

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export default {
	state: {
		defaultPermissions: [],
		permission: [],
		permissionCustomers: [],
		permissionCustomersFilter: [],
		subroleCustomers: []
	},
	mutations: {
		SET_PERMISSIONS(state, payload) {
			// ✅ клонуємо, щоб ніде випадково не мутити payload/all_permissions
			const allPermissions = deepClone(payload['all_permissions'] || []);

			// якщо хочеш, дефолт теж зберігай клонований (без normalize)
			state.defaultPermissions = allPermissions;

			if (payload.permissions && payload.permissions.length) {
				const assignPermissions = (arrDefault, arrItem) => {
					const newArr = [];
					for (const item of arrDefault) {
						const obj = arrItem.find(i => i.name === item.name);

						if (obj && obj.children && item.children) {
							const merged = Object.assign({}, item, obj); // ✅ НЕ мутимо item
							merged.children = assignPermissions(item.children, obj.children);
							newArr.push(merged);
						} else if (obj && (!obj.children || !item.children)) {
							newArr.push(Object.assign({}, item, obj)); // ✅ НЕ мутимо item
						} else {
							newArr.push(Object.assign({}, item)); // ✅ копія
						}
					}
					return newArr;
				};

				state.permission = normalizePermissionsTree(
					assignPermissions(allPermissions, payload.permissions)
				);
			} else {
				// коли немає payload.permissions — все одно робимо normalize
				state.permission = normalizePermissionsTree(
					allPermissions.map(i => Object.assign({}, i))
				);
			}
		},

		updatePermissionCustomers(state, { customer, subrole, type }) {
			if (type) {
				customer.unshift({ id: 0, value: "All" });
				state.permissionCustomersFilter = customer;
			} else {
				state.permissionCustomers = customer;
			}

			if (subrole) state.subroleCustomers = subrole;
		}
	},

	actions: {
		async onCustomersList({ commit, dispatch }, id) {
			const config = id ? { customer_id: id } : {};
			dispatch('actionPreloader', true);
			try {
				const { data } = await httpClient.post("/sub_customer", config);
				if (data && data.status) commit('updatePermissionCustomers', { ...data, type: true });
				dispatch('actionPreloader', false);
			} catch (e) {
				console.error(e);
			}
		},

		async onPermissionCustomers({ commit }) {
			try {
				const { data } = await httpClient.get("/permission/create");
				if (data && !data.status) commit("updatePermissionCustomers", { ...data, type: false });
			} catch (e) {
				console.error(e);
			}
		},

		async getRoles({ dispatch }) {
			try {
				const { data } = await httpClient.get('/permission');
				if (data) return data;
				dispatch('setAlert', { type: "error", message: "Something went wrong" });
			} catch (e) {
				console.error(e);
			}
		},

		async saveRole({ dispatch }, values) {
			try {
				const { data } = await httpClient.post('/permission', values);
				if (data) return data;
				dispatch('setAlert', { type: "error", message: "Something went wrong" });
			} catch (e) {
				console.error(e);
			}
		},

		async getPermissions({ commit, dispatch }, id) {
			try {
				const { data } = await httpClient.get(`/permission/${ id }/edit`);
				if (data) commit('SET_PERMISSIONS', data);
				else dispatch('setAlert', { type: "error", message: "Something went wrong" });
			} catch (e) {
				console.error(e);
			}
		},

		async onNewRole({ dispatch }, values) {
			try {
				const { data } = await httpClient.post('/permission/create', values);
				if (data) return data;
				dispatch('setAlert', { type: "error", message: "Something went wrong" });
			} catch (e) {
				console.error(e);
			}
		},

		async onDeleteRole({ dispatch }, { id }) {
			try {
				const { data } = await httpClient.delete(`/permission/${ id }`);
				if (data) return data;
				dispatch('setAlert', { type: "error", message: "Something went wrong" });
			} catch (e) {
				console.error(e);
			}
		}
	},

	getters: {
		getPermissionsState: s => s.permission,
		permissionCustomers: s => s.permissionCustomers,
		permissionCustomersFilter: s => s.permissionCustomersFilter,
		subroleCustomers: s => s.subroleCustomers
	},
};
